import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { SupportBulkUpdateResult } from "../../shared/support-saved-replies";
import {
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  SupportTagSchema,
  type SupportTicketUpdate,
} from "../../shared/support-schemas";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { bulkUpdateSupportTickets, supportKeys } from "./support-api";
import { PRIORITY_LABELS, STATUS_LABELS } from "./support-format";

const SELECT = "h-10 rounded-sm border border-input bg-card px-3 text-sm disabled:opacity-60";

function outcome(result: SupportBulkUpdateResult): string {
  const changed = result.updated.filter((ticket) => ticket.changed.length > 0).length;
  const refused = result.refused.length;
  const plural = (count: number) => (count === 1 ? "ticket" : "tickets");
  const done = `Changed ${String(changed)} ${plural(changed)}.`;
  return refused === 0
    ? done
    : `${done} ${String(refused)} ${plural(refused)} could not be changed.`;
}

function ChangeSelect({
  label,
  options,
  disabled,
  onChoose,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  disabled: boolean;
  onChoose: (value: string) => void;
}) {
  return (
    <select
      aria-label={label}
      className={SELECT}
      value=""
      disabled={disabled}
      onChange={(event) => {
        onChoose(event.currentTarget.value);
      }}
    >
      <option value="">{label}…</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

/**
 * One change to every selected ticket (ADR-0089). Each ticket is changed or refused on its own, and
 * the bar says how many changed.
 */
export function SupportBulkBar({
  ticketIds,
  onClear,
}: {
  ticketIds: readonly string[];
  onClear: () => void;
}) {
  const queryClient = useQueryClient();
  const [tag, setTag] = useState("");
  const change = useMutation({
    mutationFn: (update: SupportTicketUpdate) => bulkUpdateSupportTickets(ticketIds, update),
    onSuccess: async () => {
      setTag("");
      await queryClient.invalidateQueries({ queryKey: supportKeys.all });
    },
  });
  const parsedTag = SupportTagSchema.safeParse(tag);
  const busy = change.isPending;
  return (
    <div
      role="region"
      aria-label="Change selected tickets"
      className="flex flex-wrap items-center gap-3 rounded-xl bg-info-background px-4 py-3"
    >
      <span className="font-semibold">{ticketIds.length} selected</span>
      <ChangeSelect
        label="Set status"
        disabled={busy}
        options={SUPPORT_TICKET_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] }))}
        onChoose={(value) => {
          change.mutate({ status: value as SupportTicketUpdate["status"] });
        }}
      />
      <ChangeSelect
        label="Set priority"
        disabled={busy}
        options={SUPPORT_TICKET_PRIORITIES.map((value) => ({
          value,
          label: PRIORITY_LABELS[value],
        }))}
        onChoose={(value) => {
          change.mutate({ priority: value as SupportTicketUpdate["priority"] });
        }}
      />
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (parsedTag.success) change.mutate({ addTags: [parsedTag.data] });
        }}
      >
        <label htmlFor="support-bulk-tag" className="sr-only">
          Tag to add
        </label>
        <Input
          id="support-bulk-tag"
          className="w-36"
          placeholder="Add a tag"
          value={tag}
          disabled={busy}
          onChange={(event) => {
            setTag(event.target.value);
          }}
        />
        <Button type="submit" variant="outline" disabled={busy || !parsedTag.success}>
          Add tag
        </Button>
      </form>
      <Button type="button" variant="ghost" onClick={onClear}>
        Clear selection
      </Button>
      <span role="status" className="text-sm">
        {change.data ? outcome(change.data) : null}
      </span>
      {change.error ? <FieldError>{change.error.message}</FieldError> : null}
    </div>
  );
}
